import { isEmpty } from "ramda";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import GlassesSearching from "@/assets/images/search/glasses-searching.svg";
import { Typography, Modal, SearchInput, Loading } from "@/components/atoms";
import { EmptyState, NoSearchResults } from "@/components/molecules";
import { getManageClientPath } from "@/components/organisms/Clients/helpers";
import { useOperator } from "@/hooks";
import { CLIENT_SEARCH_PAGE_SIZE, useLazySearchClientQuery } from "@/redux/apis/typeahead/typeaheadApi";
import { clsx } from "@/utils";

interface HeaderSearchModalProps {
  open: boolean;
  onClose: () => void;
}

export const HeaderSearchModal = ({ open, onClose }: HeaderSearchModalProps) => {
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();
  const [searchClient, { isFetching, data }] = useLazySearchClientQuery();
  const [isFirstSearch, setIsFirstSearch] = useState(true);

  const handleClose = () => {
    onClose();
    setIsFirstSearch(true);
  };

  const handleSearch = (value?: string) => {
    const query = value ?? "";
    if (isFirstSearch && query) setIsFirstSearch(false);

    searchClient({ q: query, page_size: CLIENT_SEARCH_PAGE_SIZE });
  };

  /*
   * Mirrors HeaderSearch: the in-app Manage Client record, not the legacy PHP context-switch that
   * landed the operator on the retired client view. The modal is closed by hand because this is a
   * client-side navigation now — nothing unmounts it the way a full page load used to.
   */
  const handleSelect = (id: string) => {
    navigate(getManageClientPath(operatorId, id));
    handleClose();
  };

  const renderResults = () => {
    if (isFirstSearch) {
      return (
        <EmptyState title="" description="" image={GlassesSearching} className="-mt-20">
          <div className="-mt-20 text-center">
            <Typography variant="action" className="mb-1 block">
              Looking for something?
            </Typography>
            <Typography className="max-w-[400px] text-neutral-dark-gray">Type a keyword and we&apos;ll search through clients</Typography>
          </div>
        </EmptyState>
      );
    } else {
      if (data && !isEmpty(data)) {
        return data.map((client) => (
          <div
            key={client.id}
            className="flex w-full cursor-pointer flex-row gap-x-3 border-b border-b-neutral-mid-gray px-5 py-3.5"
            onClick={() => handleSelect(client.id)}
          >
            <div className="mt-1 flex-shrink-0">
              <img
                className="h-[24px] w-[24px] rounded-full"
                src={`https://via.placeholder.com/120x120/cccccc/969696?text=${client.name[0].toLocaleUpperCase()}`}
                alt={client.name}
              />
            </div>
            <div className="flex flex-1 flex-col overflow-hidden">
              <Typography className="text-neutral-black">{client.name}</Typography>
              <Typography className="truncate text-neutral-dark-gray" variant="small">
                {client.email}
              </Typography>
              <Typography className="text-neutral-dark-gray" variant="small">
                {client.phone}
              </Typography>
            </div>
          </div>
        ));
      } else return <NoSearchResults />;
    }
  };

  return (
    <Modal open={open} onClose={handleClose} className="h-[65vh] w-[85vw] !bg-neutral-dark-gray !p-2 md:w-[550px] lg:w-[550px]">
      <div className="flex h-full flex-col gap-1">
        <div className="flex">
          <SearchInput isLoading={isFetching} onSearch={handleSearch} placeholder="Search client" className="w-full" />
        </div>
        <div
          className={clsx("relative flex flex-1 flex-col items-center overflow-auto rounded-lg bg-white", {
            "justify-center": isFirstSearch || !data || isEmpty(data),
          })}
        >
          {isFetching && <Loading />}
          {renderResults()}
        </div>
      </div>
    </Modal>
  );
};
